import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { httpClient } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"
import type { FeatureFlagNameMap } from "./use-flags.types"

export type FeatureFlagsApiResponse = Record<string, boolean>

const FEATURE_FLAGS_PATH = "/api/v1/feature-flags"

const fetchFeatureFlags = async (
  organizationId: string | undefined
): Promise<FeatureFlagsApiResponse> => {
  const params = organizationId
    ? { organization_id: organizationId }
    : undefined
  const { data } = await httpClient.get<FeatureFlagsApiResponse>(
    FEATURE_FLAGS_PATH,
    {
      params,
    }
  )
  return data ?? {}
}

export const useFlags = (): Partial<FeatureFlagNameMap> & {
  flagsData: FeatureFlagsApiResponse | undefined
  flagsError: unknown
  flagsIsError: boolean
  flagsIsFetched: boolean
  flagsIsFetching: boolean
  flagsIsLoading: boolean
  flagsRefetch: () => Promise<unknown>
} => {
  const { activeOrganizationId } = useActiveOrganizationId()

  const organizationId = activeOrganizationId ?? undefined

  const queryResult = useQuery<FeatureFlagsApiResponse>({
    queryKey: createQueryKey(ApiQueryName.Flags, "getOne", null, {
      organizationId,
    }),
    queryFn: () => fetchFeatureFlags(organizationId),
  })

  const renamed = useRenameQueryResult(queryResult, ApiQueryName.Flags)

  const flags = (renamed.flagsData ?? {}) as Partial<FeatureFlagNameMap>

  return {
    ...renamed,
    ...flags,
  }
}
